import * as React from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, Clock, RotateCw } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { STUFEN_INFO, type ConfirmedLevel } from "@/lib/stufen-labels";
import { useAuth } from "./auth-context";
import { useLicenseStatus, useLicenseRefresh } from "./license";
import { isTraegerAdmin } from "./role-utils";

/**
 * Phase 18.E / B10 — ReadOnlyBanner.
 *
 * Mount-condition: license-status === "expired" (JWT-claim lic_exp < now).
 * Bei "active" oder "no_enforcement" (legacy-tokens / self-host) rendern wir
 * NICHTS — kein DOM, keine layout-verschiebung.
 *
 * Sichtbarkeits-strategie: sticky-top **innerhalb** des AppShell-headers
 * (vor dem brand-block), so dass scroll-content darunter weiterläuft, der
 * banner aber persistent sichtbar bleibt.
 *
 * ── B10 Conversion-Framing (zwei varianten, faktisch — keine fake-dringlichkeit) ──
 *
 *  RUHIG (default bei expired): role="region"/innen role="status" — informiert
 *  über read-only-state ohne alarm. Greift bei expired OHNE akute hitze.
 *
 *  ESKALATION (expired UND akute hitze: DWD-warnung aktiv ODER aktuelle
 *  stufe ≥ 2 "Muss"): role="alert" — der schärfste, faktische conversion-
 *  moment. Nennt die REALE aktuelle stufe (text ausschließlich aus
 *  stufen-labels.ts) + dass der gesetzliche nachweis fehlt weil der test-
 *  zeitraum ab ist. KEIN countdown, keine erfundene knappheit — nur status.
 *
 * Datenquelle stufe/DWD: eigene TanStack-query auf ["dashboard","status"]
 * (gleicher key wie DashboardRoute → cache-share, kein doppel-fetch), per
 * `enabled` NUR bei expired aktiv (keine extra-last bei active license).
 * Folgt dem ForecastTodayCard-pattern (eigenständige query statt prop-drill
 * durch AppShell → kopplung schlank + im datei-scope). Kein cache → banner
 * fällt graceful auf RUHIG zurück (fail-safe: nie eskalieren ohne beleg).
 *
 * Schreibzugriff-refresh (B10): nach (vermuteter) verlängerung triggert der
 * "Schreibzugriff aktualisieren"-button refreshLicenseStatus() (license.ts)
 * → sofortiger refresh-rotation-roundtrip statt ~15min warten. Backend bleibt
 * autoritativ (RequireActiveLicense), der button cached nur frisch.
 *
 * Backend-realität: read-routes + DSGVO-export bleiben ungated, mutations
 * werfen 403 license_expired. UI deaktiviert mutation-CTAs visuell (siehe
 * quick-add-card.tsx) um click-fail-toasts zu vermeiden.
 */

/** Minimal-shape des /v1/dashboard/status-contracts — nur was der banner braucht. */
interface DashboardStatusLite {
  current_level: "normal" | "soll" | "muss" | "kritisch";
  weather?: {
    dwd_warning_code?: number;
    dwd_warning_text?: string;
  };
  dwd_warning_code?: number | null;
  dwd_warning_text?: string | null;
}

/**
 * Enum→Index-bridge (KEIN text-mapping): API-level-string → DB-wert 0..3.
 * Identisch zur bridge in dashboard-route.tsx (LEVEL_TO_CONFIRMED). Aller
 * user-sichtbarer stufen-TEXT kommt ausschließlich aus STUFEN_INFO.
 */
const LEVEL_TO_CONFIRMED: Record<DashboardStatusLite["current_level"], ConfirmedLevel> = {
  normal: 0,
  soll: 1,
  muss: 2,
  kritisch: 3,
};

/** DWD-warnung aktiv? code>0 ODER nicht-leerer text, über beide contract-formen (flat + nested). */
function isDwdWarningActive(data: DashboardStatusLite): boolean {
  const code = data.dwd_warning_code ?? data.weather?.dwd_warning_code ?? 0;
  const text = (data.dwd_warning_text ?? data.weather?.dwd_warning_text ?? "").trim();
  return code > 0 || text !== "";
}

interface EscalationContext {
  level: ConfirmedLevel;
  dwdActive: boolean;
}

/**
 * Liest den akute-hitze-kontext für die eskalations-entscheidung.
 * Nur aktiv bei expired (sonst kein query, kein netzwerk). Liefert null
 * solange kein erfolgreicher status vorliegt → banner bleibt RUHIG
 * (fail-safe: nie ohne faktischen beleg eskalieren).
 */
function useEscalationContext(enabled: boolean): EscalationContext | null {
  const status = useQuery({
    queryKey: ["dashboard", "status"],
    queryFn: () => apiFetch<DashboardStatusLite>("/v1/dashboard/status"),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
  if (!status.data) return null;
  return {
    level: LEVEL_TO_CONFIRMED[status.data.current_level],
    dwdActive: isDwdWarningActive(status.data),
  };
}

const LICENSE_HREF =
  "mailto:hello@hitzeprotokoll.de?subject=Lizenz%20aktivieren&body=Wir%20m%C3%B6chten%20unseren%20Test-zugang%20in%20eine%20kostenpflichtige%20Lizenz%20umwandeln.";
const EXPORT_HREF_MAILTO =
  "mailto:hello@hitzeprotokoll.de?subject=DSGVO%20Art.%2020%20Datenexport&body=Bitte%20stellen%20Sie%20mir%20einen%20kompletten%20Export%20unserer%20HitzeProtokoll-Daten%20zur%20Verf%C3%BCgung.";

export function ReadOnlyBanner(): React.ReactElement | null {
  const auth = useAuth();
  const status = useLicenseStatus();
  const isExpired = status === "expired";

  // Akute-hitze-kontext NUR bei expired abfragen (enabled-gate).
  const escalation = useEscalationContext(isExpired);

  if (!isExpired) return null;

  const traeger = isTraegerAdmin(auth.user);
  const exportHref = traeger ? "/traeger/export" : EXPORT_HREF_MAILTO;

  // Eskalation NUR wenn faktisch belegt: aktive DWD-warnung ODER stufe ≥ 2 (Muss).
  // null-check inline (statt boolean-const) → TS narrowt escalation im branch.
  if (escalation !== null && (escalation.dwdActive || escalation.level >= 2)) {
    return (
      <EscalatedBanner
        level={escalation.level}
        dwdActive={escalation.dwdActive}
        traeger={traeger}
        exportHref={exportHref}
      />
    );
  }

  return <CalmBanner traeger={traeger} exportHref={exportHref} />;
}

/**
 * RUHIG — expired ohne akute hitze. role="status" (kein alarm), warm-orange-50,
 * bestehende variante. Gleiche CTA-zeile wie eskalation (primary 56px), nur
 * ohne alarm-framing.
 */
function CalmBanner({
  traeger,
  exportHref,
}: {
  traeger: boolean;
  exportHref: string;
}): React.ReactElement {
  return (
    <div
      role="region"
      aria-label="Test-zugang abgelaufen"
      className="border-b border-[color:var(--color-danger)] bg-[color:var(--color-warm-orange-50)] px-4 py-3 text-sm md:px-6"
    >
      <div
        role="status"
        className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-start gap-3">
          <AlertOctagon
            aria-hidden="true"
            className="size-5 shrink-0 text-[color:var(--color-danger)]"
          />
          <div>
            <p className="font-semibold text-[color:var(--color-text-primary)]">
              Test-zugang abgelaufen
            </p>
            <p className="text-[color:var(--color-text-secondary)]">
              Lesezugriff und Daten-Export bleiben aktiv. Neue Einträge,
              Räume und Einstellungs-Änderungen sind gesperrt.
            </p>
          </div>
        </div>
        <BannerActions traeger={traeger} exportHref={exportHref} />
      </div>
    </div>
  );
}

/**
 * ESKALATION — expired UND akute hitze. role="alert", heat-4-soft (rot),
 * primary-CTA auf touch-primary (56px). Stufen-text 100% aus STUFEN_INFO.
 *
 * Conversion-framing ehrlich: nennt die REALE stufe + DWD-warnung (wenn da)
 * + die faktische lücke (nachweis fehlt weil testzeitraum ab). Kein countdown,
 * keine erfundene knappheit. Doppelt kodiert (icon + klartext + farbe) →
 * color-blind-safe.
 */
function EscalatedBanner({
  level,
  dwdActive,
  traeger,
  exportHref,
}: {
  level: ConfirmedLevel;
  dwdActive: boolean;
  traeger: boolean;
  exportHref: string;
}): React.ReactElement {
  const info = STUFEN_INFO[level];
  // Headline: faktischer status. Stufen-name + (Soll/Muss/Pflicht-zusatz) aus single-source.
  const stufenText = info.zusatz ? `${info.short} (${info.zusatz})` : info.short;

  // Begründungs-satz: warum jetzt kritisch — der gesetzliche nachweis fehlt.
  // Bei aktiver DWD-warnung zusätzlich benennen (faktisch, nicht dramatisiert).
  const reason = dwdActive
    ? "Aktuell liegt eine DWD-Hitzewarnung vor und der gesetzliche Hitzeschutz-Nachweis lässt sich nicht dokumentieren, weil der Testzeitraum abgelaufen ist."
    : "Der gesetzliche Hitzeschutz-Nachweis lässt sich nicht dokumentieren, weil der Testzeitraum abgelaufen ist.";

  return (
    <div
      role="alert"
      aria-label="Hitzeschutz-Dokumentation gesperrt — Testzeitraum abgelaufen"
      className="border-b-2 border-[color:var(--color-danger)] bg-[color:var(--color-heat-4-soft)] px-4 py-3.5 text-sm md:px-6"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3">
          <AlertOctagon
            aria-hidden="true"
            strokeWidth={2.25}
            className="mt-0.5 size-6 shrink-0 text-[color:var(--color-danger)]"
          />
          <div>
            <p className="font-bold text-[color:var(--color-text-primary)]">
              Aktuell Stufe {level} · {stufenText} — Dokumentation gesperrt
            </p>
            <p className="mt-0.5 leading-snug text-[color:var(--color-text-primary)]">
              {reason} Lesezugriff und Export bleiben erhalten; neue Messungen
              und Maßnahmen-Nachweise sind gesperrt.
            </p>
          </div>
        </div>
        <BannerActions
          traeger={traeger}
          exportHref={exportHref}
          ctaLabel="Jetzt aktivieren"
        />
      </div>
    </div>
  );
}

/**
 * Geteilte CTA-zeile: primary "aktivieren" (mailto, Y1 manual) + sekundär
 * export + "Schreibzugriff aktualisieren"-refresh-trigger (B10).
 *
 * Touch-targets: primary-CTA (der conversion-hebel) auf --size-touch-primary
 * (56px, lese-brillen-puffer); sekundär-aktionen auf --size-touch-default
 * (48px) — konsistent mit dem projektweiten design-system (56px ist für
 * primary/FAB reserviert, tokens.css). Die >=56px-anforderung gilt dem
 * primären aktivieren-target.
 */
function BannerActions({
  traeger,
  exportHref,
  ctaLabel = "Lizenz aktivieren",
}: {
  traeger: boolean;
  exportHref: string;
  ctaLabel?: string;
}): React.ReactElement {
  const { refresh, isRefreshing } = useLicenseRefresh();

  return (
    <div className="flex flex-shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
      <a
        href={LICENSE_HREF}
        style={{ minHeight: "var(--size-touch-primary)" }}
        className="inline-flex items-center justify-center rounded-md bg-[color:var(--color-brand-primary-strong)] px-5 text-sm font-semibold text-white transition-colors hover:bg-[color:var(--color-warm-orange-800)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-border-focus)]"
      >
        {ctaLabel}
      </a>
      {traeger ? (
        <Link
          to={exportHref}
          style={{ minHeight: "var(--size-touch-default)" }}
          className="inline-flex items-center justify-center rounded-md border border-[color:var(--color-border)] bg-white px-4 text-sm font-medium text-neutral-700 hover:bg-[color:var(--color-surface-sunken)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-border-focus)]"
        >
          Daten exportieren
        </Link>
      ) : (
        <a
          href={exportHref}
          style={{ minHeight: "var(--size-touch-default)" }}
          className="inline-flex items-center justify-center rounded-md border border-[color:var(--color-border)] bg-white px-4 text-sm font-medium text-neutral-700 hover:bg-[color:var(--color-surface-sunken)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-border-focus)]"
        >
          Daten exportieren
        </a>
      )}
      <button
        type="button"
        onClick={() => void refresh()}
        disabled={isRefreshing}
        style={{ minHeight: "var(--size-touch-default)" }}
        aria-label="Schreibzugriff aktualisieren — prüft, ob die Lizenz inzwischen verlängert wurde"
        className="inline-flex items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium text-[color:var(--color-text-primary)] underline-offset-4 hover:underline disabled:cursor-progress disabled:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-border-focus)]"
      >
        {isRefreshing ? (
          <Clock aria-hidden="true" strokeWidth={2.25} className="size-4 animate-spin" />
        ) : (
          <RotateCw aria-hidden="true" strokeWidth={2.25} className="size-4" />
        )}
        {isRefreshing ? "Wird geprüft…" : "Schreibzugriff aktualisieren"}
      </button>
    </div>
  );
}
